import type { Breadcrumb } from '@sentry/angular';
import { maskId } from './log-safe';

/**
 * Regex targeting opaque identifier segments inside URL paths/query strings.
 * - UUID-shaped: 16+ hex/dash chars (e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890)
 * - Long opaque: 20+ alphanumeric/underscore/dash chars
 * Short slugs (e.g. ':storeslug', 'norgesgruppen') intentionally NOT matched.
 * Note: Angular asset bundle hashes (e.g. main.<hash>.js) will also be masked —
 * acceptable trade-off since maskId preserves the last 4 chars for log correlation.
 */
const OPAQUE_SEGMENT_RE = /[a-f0-9-]{16,}|[A-Za-z0-9_-]{20,}/gi;

/**
 * Mask UUIDs and long opaque IDs inside a URL string.
 * Each matching segment is replaced via maskId() (returns '...' + last 4 chars).
 * Pure function — safe for use in Sentry beforeBreadcrumb.
 */
export function maskUrl(url: string): string {
  if (!url) return url;
  return url.replace(OPAQUE_SEGMENT_RE, (match) => maskId(match));
}

/**
 * Sanitize a Sentry breadcrumb before it is recorded.
 * Returns null to drop the breadcrumb, or a (possibly mutated) Breadcrumb to keep.
 *
 * D-07 policy:
 * - Drop ui.input and ui.keypress (typed values are PII)
 * - Mask data.url / data.from / data.to for xhr, fetch, navigation
 * - Pass through everything else (console, ui.click, etc.) unchanged
 */
export function sanitizeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'ui.input' || breadcrumb.category === 'ui.keypress') {
    return null;
  }
  if (
    breadcrumb.category === 'xhr' ||
    breadcrumb.category === 'fetch' ||
    breadcrumb.category === 'navigation'
  ) {
    const data = breadcrumb.data;
    if (data) {
      if (typeof data['url'] === 'string') {
        data['url'] = maskUrl(data['url']);
      }
      if (typeof data['from'] === 'string') {
        data['from'] = maskUrl(data['from']);
      }
      if (typeof data['to'] === 'string') {
        data['to'] = maskUrl(data['to']);
      }
    }
  }
  return breadcrumb;
}
